import { Helmet } from 'react-helmet';

interface MetadataProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  keywords?: string;
  pageType?: 'home' | 'about' | 'videos' | 'services' | 'contact';
}

const Metadata: React.FC<MetadataProps> = ({
  title = "Daryian Kelton - Drums & Visuals",
  description = "Professional drummer, musician, photographer, and videographer stretching the limits of creativity through multiple mediums.",
  image = "/photos/webp/sm23-rockwood5.webp",
  url = "https://daryiankelton.com",
  type = "website",
  keywords = "drummer, session drummer, music production, content creation, drum repair, drum tuning, videographer",
  pageType = "home"
}) => {
  const logoUrl = "/logo/DK Monogram - MUST_BLK.svg";
  const faviconUrl = "/logo/DK Monogram - MUST_BLK.png";

  // Page-specific schema data
  const getPageSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": url,
      "name": "Daryian Kelton",
      "url": url,
      "image": `${url}${image}`,
      "description": description,
      "sameAs": [
        "https://youtube.com/@DaryianKelton",
        "https://instagram.com/drummerboy_x",
        "https://facebook.com/drummerboy-xd",
        "https://seriousmattersnj.com"
      ],
      "logo": {
        "@type": "ImageObject",
        "url": `${url}${logoUrl}`,
        "width": "512",
        "height": "512"
      }
    };

    switch (pageType) {
      case 'services':
        return {
          ...baseSchema,
          "offers": [
            {
              "@type": "Offer",
              "name": "Session Drummer",
              "description": "Professional drum performances for recording sessions"
            },
            {
              "@type": "Offer",
              "name": "Music Production",
              "description": "Complete music production services from arrangement to final polish"
            },
            {
              "@type": "Offer",
              "name": "Content Creation",
              "description": "Professional video and photo production services"
            },
            {
              "@type": "Offer",
              "name": "Drum Repair & Tuning",
              "description": "Expert drum maintenance, repair, and tuning services"
            }
          ]
        };
      case 'videos':
        return {
          ...baseSchema,
          "@type": ["Person", "VideoObject"],
          "contentUrl": "https://youtube.com/@DaryianKelton",
          "thumbnailUrl": `${url}${image}`,
          "uploadDate": new Date().toISOString()
        };
      case 'about':
        return {
          ...baseSchema,
          "@type": ["Person", "MusicGroup"],
          "genre": ["Rock", "Metal", "Progressive"],
          "member": {
            "@type": "Person",
            "name": "Daryian Kelton",
            "roleName": "Drummer"
          }
        };
      case 'contact':
        return {
          ...baseSchema,
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "contact@daryiankelton.com"
          }
        };
      default:
        return baseSchema;
    }
  };

  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />

      {/* Favicon */}
      <link rel="icon" type="image/svg+xml" href={logoUrl} />
      <link rel="alternate icon" type="image/png" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />

      {/* OpenGraph metadata */}
      <meta property="og:site_name" content="Daryian Kelton" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${url}${image}`} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card metadata */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${url}${image}`} />
      <meta name="twitter:creator" content="@drummerboy_x" />

      {/* Additional SEO metadata */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Daryian Kelton" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#000000" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(getPageSchema())}
      </script>
    </Helmet>
  );
};

export default Metadata; 